import React from "react";
import { PrismicRichText } from "@prismicio/react";
import { graphql, Link } from "gatsby";
import Seo from "../components/includes/seo";
import Layout from "../components/includes/layout";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { QHFacebookShareButton } from "../components/common/facebook-share-button";
import { QHXShareButton } from "../components/common/x-share-button";
import { QHLinkedinShareButton } from "../components/common/linkedin-share-button";

export const navInsiderQuery = graphql`
  query NavInsiderQuery($id: String) {
    prismicNavigationInsider(id: { eq: $id }) {
      url
      data {
        content {
          richText
          text
        }
        date_published(formatString: "MMMM dd, YYYY")
        description {
          text
        }
        meta_image {
          gatsbyImageData(layout: CONSTRAINED)
          alt
          url
        }
        hide
        nav_insider_check
        resource_label
        resource_title {
          richText
          text
        }
        title {
          text
        }
      }
    }
    prismicHeaderCallout {
      _previewable
      data {
        label {
          text
        }
        link {
          text
        }
        title {
          richText
        }
      }
    }
    prismicNavigation {
      _previewable
      id
      data {
        entry
        main_nav_items {
          nav_link {
            url
          }
          nav_label
          resource {
            document {
              ... on PrismicPressRelease {
                id
                data {
                  resource_label
                  resource_title {
                    text
                    richText
                  }
                  description {
                    text
                  }
                  meta_image {
                    gatsbyImageData
                    alt
                  }
                }
                url
              }
              ... on PrismicResource {
                id
                data {
                  resource_label
                  resource_title {
                    text
                    richText
                  }
                  description {
                    text
                  }
                  meta_image {
                    gatsbyImageData
                    alt
                  }
                }
                url
              }
            }
          }
          drop_down {
            id
            document {
              ... on PrismicNavigationItem {
                id
                data {
                  theme
                  sub_nav_item {
                    icon {
                      url
                      alt
                      gatsbyImageData(layout: CONSTRAINED)
                    }
                    nav_context {
                      text
                      richText
                    }
                    nav_label
                    sub_nav {
                      document {
                        ... on PrismicSubNavigationItem {
                          id
                          data {
                            sub_nav_item {
                              nav_label
                              nav_link {
                                url
                              }
                            }
                          }
                        }
                      }
                    }
                    nav_link {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const NavInsider = (props) => {
  const url =
    "https://quantum-health.com" +
    props.data.prismicNavigationInsider.url +
    "/";

  const text = props.data.prismicNavigationInsider.data.content.text;
  const wpm = 300;
  const words = text.trim().split(/\s+/).length;
  const time = Math.ceil(words / wpm);

  return (
    <Layout
      headCall={props.data.prismicHeaderCallout.data}
      nav={props.data.prismicNavigation.data}
    >
      <section
        className="pr relative gutter top bg-gradient-to-t from-grey-light to-white"
        id="main"
      >
        <svg
          id="visual"
          viewBox="0 0 960 540"
          preserveAspectRatio="none"
          className="absolute hidden md:block w-full -bottom-px"
          width="100%"
          height="540"
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
        >
          <path
            d="M0 421L40 435.7C80 450.3 160 479.7 240 474.5C320 469.3 400 429.7 480 416.5C560 403.3 640 416.7 720 410.3C800 404 880 378 920 365L960 352L960 541L920 541C880 541 800 541 720 541C640 541 560 541 480 541C400 541 320 541 240 541C160 541 80 541 40 541L0 541Z"
            fill="#fff"
            strokeLinecap="round"
            strokeLinejoin="miter"
          ></path>
        </svg>

        <div className="banner wrapper flex flex-col md:flex-row justify-between items-center md:pt-8 gap-12 md:gap-24">
          <div className="w-full pt-8 md:w-2/4 text-grey">
            {props.data.prismicNavigationInsider.data.resource_label ? (
              <div className="label text-accessible-green bg-opacity-10 font-bold uppercase tracking-widest text-xs mb-1 md:mb-2">
                {props.data.prismicNavigationInsider.data.resource_label}
              </div>
            ) : (
              ""
            )}
            <h1>
              <PrismicRichText
                field={
                  props.data.prismicNavigationInsider.data.resource_title
                    .richText
                }
              />
            </h1>
            <div className="text-grey-darker flex flex-col md:flex-row md:gap-4 md:items-center">
              <span>
                <i className="fa-regular fa-clock text-grey-dark mr-1" /> {time}{" "}
                minute read
              </span>
              <span className="text-2xl hidden md:block text-grey-md">•</span>
              <Link
                className="btn text-base px-0 pr-8 text-teal flex items-center"
                to="/navigation-insider/"
              >
                Navigation Insider
              </Link>
            </div>
            <div className="soci md:mb-12">
              <p>SHARE</p>
              <QHFacebookShareButton url={url} />
              <QHXShareButton url={url} />
              <QHLinkedinShareButton url={url} />
            </div>
          </div>
          <div className="relative md:top-6 mb-6 w-full md:mb-0 md:w-2/4 flex">
            <GatsbyImage
              loading="eager"
              image={getImage(
                props.data.prismicNavigationInsider.data.meta_image
              )}
              className="rounded-3xl shadow-2xl self-end"
              alt={
                props.data.prismicNavigationInsider.data.meta_image.alt
                  ? props.data.prismicNavigationInsider.data.meta_image.alt
                  : props.data.prismicNavigationInsider.data.resource_title.text
              }
            />
            
          </div>
        </div>
      </section>
      <section className="pr ni gutter md:top">
        <div className="wrapper flex flex-col md:flex-row gap-12 md:gap-24">
          <div className="w-full md:w-3/5">
            <PrismicRichText
              field={props.data.prismicNavigationInsider.data.content.richText}
            />
          </div>
          <div className="sticky top-6 w-full md:w-2/5 rounded-2xl p-8 bg-grey-light flex flex-col items-center self-start text-center">
            <i className="fa-regular fa-envelope text-3xl mb-3 text-green"></i>
            <h3 className="!mb-2 !mt-0">
              Relevant, actionable health benefits insights
            </h3>
            <p className="!text-base">Subscribe to Navigation Insider</p>
            <a
              href="/navigation-insider-subscription/"
              className="btn bg-yellow"
            >
              Subscribe
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NavInsider;

export const Head = (props) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id":
        "https://quantum-health.com" +
        props.data.prismicNavigationInsider.url +
        "/",
    },
    headline: props.data.prismicNavigationInsider.data.title.text,
    description: props.data.prismicNavigationInsider.data.description.text,
    image: props.data.prismicNavigationInsider.data.meta_image.url,
    author: {
      "@type": "Organization",
      name: "Quantum Health",
    },
  };

  return (
    <Seo
      hide={props.data.prismicNavigationInsider.data.hide}
      title={
        props.data.prismicNavigationInsider.data.title.text +
        " - Quantum Health"
      }
      desc={props.data.prismicNavigationInsider.data.description.text}
      metaImage={props.data.prismicNavigationInsider.data.meta_image.url}
      url={props.data.prismicNavigationInsider.url}
      schemaMarkup={schema}
    />
  );
};
